/**
 * Session-level consent manager for third-party asset loading.
 *
 * A CMP provider feeds this gate as soon as a consent decision is known:
 *
 * - stored consent found at startup -> decision from the stored categories
 * - no stored consent -> decision when the user answers the banner
 * - consent integration disabled (kill switch) -> fully granted,
 *   preserving pre-CMP behaviour
 * - SDK failure -> fully denied (fail-closed)
 *
 * The CMP provider on Smart TV is the OneTrust CTV provider
 * (dal/pub/snowpiercer/consent/OneTrustCTV.tsx); it also records the
 * kill-switch-off case as "integration disabled, everything granted". On the
 * web it is Cookiebot (shared/libs/offspring/libs/cookiebotConsent.ts). The
 * manager is deliberately vendor agnostic: swapping the CMP means swapping
 * the provider, and no consumer changes.
 *
 * The manager is a module singleton. During server rendering (Next.js) that
 * singleton is shared across every request and can never hold one visitor's
 * answer, so the readers in consentPolicy.ts and advid/ fail closed on the
 * server. Consent-dependent output belongs on the client.
 *
 * Consumers (see assets.ts) await the FIRST decision of the session before
 * loading third-party scripts, because a loaded script cannot be unloaded
 * again. When the user CHANGES their consent later in the session (via the
 * preference center), the provider reloads the app so that the set of loaded
 * scripts always matches the current consent - both for revocations and for
 * newly granted categories. `getCurrentConsentDecision()` exposes the live
 * value for consumers that make per-call decisions (e.g. request parameters).
 *
 * Read a single category through `isConsentCategoryAllowed()` in
 * consentPolicy.ts rather than inspecting the decision directly - it applies
 * the rule the raw decision does not carry: a missing decision counts as
 * declined.
 */

/**
 * The consent categories other teams read from this gate. These names are the
 * contract - they are what the CMP calls the categories, so nothing has to be
 * translated on the way in or out:
 *
 * - `Preferences` - remembers choices the user already made (theme, region,
 *                   player settings)
 * - `Statistics`  - aggregated audience measurement
 * - `Marketing`   - profiling for personalized advertising
 *
 * Plus the IAB TCF groups the tracking contract reports next to them. Only a
 * CMP with the TCF framework enabled (OneTrust) can grant these; Cookiebot does
 * not expose them, so there they are always declined:
 *
 * - `IabPurpose1` - TCF purpose 1 "Store and/or access information on a device"
 * - `IabStack1`   - TCF stack 1 "Precise geolocation data, and identification
 *                   through device scanning"
 * - `IabStack42`  - TCF stack 42 "Personalised advertising and content,
 *                   advertising and content measurement, audience research and
 *                   services development"
 *
 * Strictly necessary storage is deliberately not here. It is exempt from
 * consent under the ePrivacy Directive, so there is nothing for a consent manager
 * to answer: it can never be denied, and must not be storable as denied.
 */
export type ConsentCategory =
  | 'Preferences'
  | 'Statistics'
  | 'Marketing'
  | 'IabPurpose1'
  | 'IabStack1'
  | 'IabStack42';

/**
 * Every category, in the order the tracking contract reports them. The single
 * place to iterate a {@link ConsentDecision}.
 */
export const CONSENT_CATEGORIES: ReadonlyArray<ConsentCategory> = [
  'Statistics',
  'Preferences',
  'Marketing',
  'IabPurpose1',
  'IabStack1',
  'IabStack42',
];

/**
 * The CMP group ids each category maps to (OneTrust convention). A CMP
 * provider translates its own signal into a {@link ConsentDecision}, so this
 * is the single place that has to change for a different vendor. `C0001` is
 * absent for the same reason strictly necessary storage is: it is exempt.
 */
export const CONSENT_CATEGORY_IDS: Record<ConsentCategory, string> = {
  Preferences: 'C0003',
  Statistics: 'C0002',
  Marketing: 'C0004',
  IabPurpose1: 'IAB2V2_1',
  IabStack1: 'V2STACK1',
  IabStack42: 'V2STACK42',
};

/**
 * The decision the user made, one flag per category. The keys are the category
 * names, so a category can index the decision directly.
 */
export type ConsentDecision = {
  /** Preference/personalization storage may be used. */
  Preferences: boolean;
  /** Performance/analytics tooling may load. */
  Statistics: boolean;
  /**
   * Advertising consent. Note: this does NOT gate whether GPT loads - ads are
   * part of the service and always load once a decision exists. It gates
   * user-scoped personalization inputs (e.g. PPID and advid); ad
   * personalization itself is governed by the TCF signal on the ad request.
   */
  Marketing: boolean;
  /** TCF purpose 1: store and/or access information on a device. */
  IabPurpose1: boolean;
  /** TCF stack 1: precise geolocation and identification through device scanning. */
  IabStack1: boolean;
  /** TCF stack 42: personalised ads/content, measurement, audience research, development. */
  IabStack42: boolean;
};

/**
 * Every category granted. Recorded by the CMP provider when the consent
 * integration is disabled (kill switch off), preserving pre-CMP behaviour.
 *
 * Frozen because the manager stores the decision it is handed by reference, so a
 * consumer mutating this would quietly rewrite the starting state.
 */
export const ALL_CONSENT_GRANTED: Readonly<ConsentDecision> = Object.freeze({
  Preferences: true,
  Statistics: true,
  Marketing: true,
  IabPurpose1: true,
  IabStack1: true,
  IabStack42: true,
});

/** Every category declined. What a CMP that has been dismissed amounts to. */
export const ALL_CONSENT_DENIED: Readonly<ConsentDecision> = Object.freeze({
  Preferences: false,
  Statistics: false,
  Marketing: false,
  IabPurpose1: false,
  IabStack1: false,
  IabStack42: false,
});

/** Whether two decisions grant exactly the same categories. */
export function isSameConsentDecision(a: ConsentDecision, b: ConsentDecision): boolean {
  return CONSENT_CATEGORIES.every(category => a[category] === b[category]);
}

let current: ConsentDecision | null = null;
let integrationEnabled = false;
export type ConsentTrackingStatus = 'accepted' | 'declined' | 'notDecided';

// Permission fallbacks (disabled CMP / failed initialization) are not choices.
let trackingStatus: ConsentTrackingStatus = 'notDecided';
const decisionListeners = new Set<() => void>();

export function getConsentTrackingStatus(): ConsentTrackingStatus {
  return trackingStatus;
}

/** The CMP adapter records whether a real choice has been confirmed. */
export function setConsentTrackingStatus(status: ConsentTrackingStatus): void {
  trackingStatus = status;
}

let consentDetails: string | undefined;

/**
 * The CMP's per-group consent record for tracking, in the cross-platform
 * format produced by `formatConsentDetails()` (consentDetails.ts):
 * `<cmp>|statistics:0,preferences:1,...`. `undefined` until a CMP provider
 * on this platform records it, and whenever the integration is disabled (no
 * CMP, no claim); consumers must then omit the key entirely.
 */
export function getConsentDetails(): string | undefined {
  return consentDetails;
}

/** Record the per-group consent record. Only the CMP provider should call this. */
export function setConsentDetails(details: string | undefined): void {
  consentDetails = details;
}

/** Observe current permissions, including decisions arriving after startup timeout. */
export function subscribeConsentDecision(listener: () => void): () => void {
  decisionListeners.add(listener);
  return () => {
    decisionListeners.delete(listener);
  };
}
let resolveFn: (decision: ConsentDecision) => void = () => {};

let decisionPromise = new Promise<ConsentDecision>(resolve => {
  resolveFn = resolve;
});

/**
 * Resolves with the first consent decision of this app session.
 * Never rejects. Use for one-time gating of third-party script loading.
 */
export function getConsentDecision(): Promise<ConsentDecision> {
  return decisionPromise;
}

/**
 * The current consent decision, or null while the user has not decided yet.
 * Use for per-call decisions (request headers/parameters), NOT for gating
 * script loading (await getConsentDecision() for that).
 */
export function getCurrentConsentDecision(): ConsentDecision | null {
  return current;
}

/**
 * Record the session consent decision. The first call resolves
 * getConsentDecision(); later calls only update the current value (the
 * provider reloads the app on a changed decision, see module docs).
 */
export function setConsentDecision(decision: ConsentDecision): void {
  const isFirst = current === null;
  current = decision;
  if (isFirst) {
    resolveFn(decision);
  }
  decisionListeners.forEach(listener => listener());
}

/** Whether a consent decision has been made for this session. */
export function isConsentDecisionResolved(): boolean {
  return current !== null;
}

/**
 * Whether the consent integration (CMP) is active this session. Set by the
 * CMP provider at startup (kill switch on + app id configured). Consumers
 * that ASSERT consent outward (e.g. consent request headers) must stay silent
 * when the integration is disabled - no CMP means no claim, and possibly
 * stale persisted consent data from an earlier enabled session.
 */
export function setConsentIntegrationEnabled(enabled: boolean): void {
  integrationEnabled = enabled;
  // No CMP means no claim: neither a choice nor a per-group record.
  if (!enabled) {
    trackingStatus = 'notDecided';
    consentDetails = undefined;
  }
  decisionListeners.forEach(listener => listener());
}

export function isConsentIntegrationEnabled(): boolean {
  return integrationEnabled;
}

let tcfConsentString: string | undefined;

/**
 * The raw IAB TCF consent string, for consumers that pass the full consent
 * record onward (e.g. server-side ad insertion, backend requests). Set by the
 * CMP provider; `undefined` until a CMP is integrated on this platform or
 * while no consent has been recorded. Consumers must omit it entirely when
 * undefined - never send an empty string.
 */
export function getTcfConsentString(): string | undefined {
  return tcfConsentString;
}

/** Record the current TCF string. Only the CMP provider should call this. */
export function setTcfConsentString(tcString: string | undefined): void {
  tcfConsentString = tcString;
}

let advertisingIdentifiersAllowed = false;

/**
 * Whether stable user-scoped advertising identifiers (advid/PPID) may be
 * exposed. Per the advid solution design this requires FULL consent: every
 * category present in the CMP template must be accepted ("any NO means NO to
 * all") - a stricter rule than any single category. The CMP provider owns
 * that evaluation (it knows which categories its template actually contains)
 * and records the verdict here. Fails closed until the provider speaks;
 * a disabled integration preserves pre-CMP behaviour (allowed).
 *
 * Always `false` during server rendering - see the module docs.
 */
export function areAdvertisingIdentifiersAllowed(): boolean {
  if (typeof window === 'undefined') return false;
  if (!integrationEnabled) return true;
  return advertisingIdentifiersAllowed;
}

/** Record the advertising-identifier verdict. Only the CMP provider should call this. */
export function setAdvertisingIdentifiersAllowed(allowed: boolean): void {
  advertisingIdentifiersAllowed = allowed;
}

let personalizedAdsAllowed = false;

/**
 * Whether personalized advertising is switched on for this session. This is
 * NOT a consent category: it is the `allow_personalized_ads` rollout flag from
 * the consent endpoint (prompts-graph `GET /consent`, see
 * dal/pub/snowpiercer/consent/remoteConsentConfig.ts), recorded by the CMP
 * provider once fetched. Consumers combine it with the user's consent - a
 * `true` here never grants anything the CMP has not.
 *
 * Defaults to `true`: a platform that does not fetch the endpoint (the web)
 * keeps its current behaviour. Once a provider fetches it, the endpoint's
 * answer wins - and the fetch itself fails closed (see remoteConsentConfig),
 * so a missing key or an unreachable endpoint records `false`.
 */
export function isPersonalizedAdsAllowed(): boolean {
  return personalizedAdsAllowed;
}

/** Record the `allow_personalized_ads` flag. Only the CMP provider should call this. */
export function setPersonalizedAdsAllowed(allowed: boolean): void {
  personalizedAdsAllowed = allowed;
}

/**
 * Reload the app after a user-changed consent decision. Loaded third-party
 * scripts cannot be unloaded, so a reload is the only way to make the loaded
 * scripts match the new consent. Lives here (rather than inline in the
 * provider) so tests can mock it.
 */
export function reloadForConsentChange(): void {
  if (typeof window === 'undefined') return;
  window.location.reload();
}

let addtlConsentString: string | undefined;

/**
 * The raw IAB TCF additional consent string, for consumers that pass the full consent
 * record onward (e.g. server-side ad insertion, backend requests). Set by the
 * CMP provider; `undefined` until a CMP is integrated on this platform or
 * while no consent has been recorded. Consumers must omit it entirely when
 * undefined - never send an empty string.
 */
export function getAddtlConsentString(): string | undefined {
  return addtlConsentString;
}

/** Record the current TCF additional consent string. Only the CMP provider should call this. */
export function setAddtlConsentString(acString: string | undefined): void {
  addtlConsentString = acString;
}

/**
 * TEST ONLY: re-arm the manager. The manager is intentionally a module singleton in
 * production; tests need isolation between cases.
 */
export function __resetConsentDecisionForTests(): void {
  current = null;
  integrationEnabled = false;
  tcfConsentString = undefined;
  addtlConsentString = undefined;
  advertisingIdentifiersAllowed = false;
  personalizedAdsAllowed = false;
  trackingStatus = 'notDecided';
  consentDetails = undefined;
  decisionListeners.clear();
  decisionPromise = new Promise<ConsentDecision>(resolve => {
    resolveFn = resolve;
  });
}
